/**
 * Shared helpers, types, and defaults for the tool-creation form: translation between the
 * partner-facing "inputs" table (one row per argument the agent passes) and the backend
 * tool-definition shape (`request_schema` JSON Schema + `request_mapping` JSONata), plus the
 * blank draft and credential/auth defaults the form builds on. Keeping these here keeps
 * `ToolForm` declarative and lets the round-trips be unit-tested in isolation.
 */
import {
  CONTEXT_REF_RE,
  FILLABLE_CONTEXT_KEYS,
  collectTemplateStrings,
  deriveAgentArgsSchema,
  extractReferences,
  type FillableContextKey,
} from '@reach/shared-types/mcp-context';
import type { ToolDefinitionInput } from '../../lib/api';

export type ToolInputType = 'string' | 'number' | 'boolean';

/** Where a field rides in the request: in the JSON body or as a query-string parameter. */
export type InputLocation = 'body' | 'query';

export interface ToolInputRow {
  name: string;
  type: ToolInputType;
  required: boolean;
  description?: string;
  /** When set, Reach fills this field from session context; deriveAgentArgsSchema strips it from every agent-facing surface, so the agent never sees it. */
  source?: ContextSource;
  /** Overrides where the field rides. Unset → the method's natural location (body for non-GET, query for GET). GET ignores this (no body). */
  location?: InputLocation;
}

/** The narrow shape we read out of the (loosely-typed) `auth_config` JSON column. */
export interface ToolAuthConfig {
  credentialSlot?: string;
  headerName?: string;
}

const argTemplate = (name: string): string => `{{args.${name}}}`;

/**
 * Default credential slot for new tools. The execution engine exposes every slot to
 * request templates as `{{secrets.<slot>}}`, so the slot name doubles as the variable a
 * developer references — `secret` reads better there than the legacy `primary`.
 */
export const DEFAULT_CREDENTIAL_SLOT = 'secret';

/** The variable a stored secret is referenced by inside the request, e.g. `{{secrets.secret}}`. */
export function secretVariable(slot: string): string {
  return `{{secrets.${slot}}}`;
}

/** A Reach-resolved session value a tool input can be sourced from instead of the agent. */
export type ContextSource = FillableContextKey;

/** The `{{context.<key>}}` token the execution engine fills in for a Reach-sourced field. */
export function contextToken(source: ContextSource): string {
  return `{{context.${source}}}`;
}

/**
 * Dashboard label and locked-Required explainer per Reach source, keyed off FILLABLE_CONTEXT_KEYS
 * (@reach/shared-types) so adding a context value forces both here. The token is derived from the
 * key, never restated.
 */
export const CONTEXT_SOURCES: Record<ContextSource, { label: string; requiredHint: string }> = {
  tenantExternalId: {
    label: 'Tenant ID',
    requiredHint:
      "Reach fills this in with the business's ID and sends it on every call, so it can't be made optional.",
  },
  participantPhone: {
    label: 'Customer phone',
    requiredHint:
      "Reach fills this in with the customer's phone number and sends it on every call, so it can't be made optional.",
  },
};

/** Source dropdown order — the canonical key order from shared-types. */
export const CONTEXT_SOURCE_ORDER: ContextSource[] = [...FILLABLE_CONTEXT_KEYS];

const TOKEN_TO_SOURCE: Record<string, ContextSource> = Object.fromEntries(
  CONTEXT_SOURCE_ORDER.map(source => [contextToken(source), source]),
);

/** Single-token exports for surfaces that reference one source by name (e.g. the test-runner hint). */
export const TENANT_EXTERNAL_ID_TOKEN = contextToken('tenantExternalId');
export const PARTICIPANT_PHONE_TOKEN = contextToken('participantPhone');

/** The context source a request value maps to, or `undefined` when it's a normal `{{args.*}}` value. */
function contextSourceForValue(value: unknown): ContextSource | undefined {
  return typeof value === 'string' ? TOKEN_TO_SOURCE[value] : undefined;
}

/** Field name → context source, read from the request mapping's body/query blocks. */
function contextSourceByFieldName(
  mapping: Record<string, unknown> | null | undefined,
): Map<string, ContextSource> {
  const result = new Map<string, ContextSource>();
  const mappingObject = (mapping ?? {}) as Record<string, unknown>;
  for (const location of ['body', 'query'] as const) {
    const block = mappingObject[location];
    if (!block || typeof block !== 'object') continue;
    for (const [name, value] of Object.entries(block)) {
      const source = contextSourceForValue(value);
      if (source) result.set(name, source);
    }
  }
  return result;
}

/** Field name → the request location (body/query) it's mapped in. */
function locationByFieldName(
  mapping: Record<string, unknown> | null | undefined,
): Map<string, InputLocation> {
  const result = new Map<string, InputLocation>();
  const mappingObject = (mapping ?? {}) as Record<string, unknown>;
  for (const location of ['body', 'query'] as const) {
    const block = mappingObject[location];
    if (!block || typeof block !== 'object') continue;
    for (const name of Object.keys(block)) result.set(name, location);
  }
  return result;
}

/**
 * A blank tool draft: empty user-facing fields plus the server-side defaults the form
 * keeps but does not expose. The single source of truth for those defaults — both the new
 * draft and the worked examples build on it, so they can't drift apart.
 */
export const BLANK_TOOL_DRAFT: ToolDefinitionInput = {
  display_name: '',
  description: '',
  http_method: 'POST',
  base_url: '',
  path_template: '',
  auth_type: 'custom_header',
  auth_config: { credentialSlot: DEFAULT_CREDENTIAL_SLOT, headerName: 'x-api-key' },
  credentials: {},
  request_schema: { type: 'object', properties: {}, required: [] },
  request_mapping: { body: {} },
  response_mapping: null,
  schema_mapping_refs: null,
  required_scope: 'partner_user',
  cacheable: false,
  cache_ttl_ms: 60000,
  claim_enforcement: null,
  timeout_ms: 5000,
  retry_policy: null,
  annotations: { openWorldHint: true },
  variables: null,
  status: 'active',
};

/** Arg names must be referenceable as `{{args.<name>}}` (the runtime regex allows word chars + hyphen). */
export const INPUT_NAME_PATTERN = /^[\w-]+$/;

/**
 * The tool name is sent as the API slug verbatim, and the partner API enforces lowercase
 * snake_case starting with a letter. Validate the same rule here so a bad name fails inline
 * instead of on submit. Max length mirrors mcp-core's MAX_SLUG_LENGTH (64 − 36-char partner
 * UUID − 1 separator).
 */
export const TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
export const MAX_TOOL_NAME_LENGTH = 27;

export function validateToolName(name: string): string | null {
  if (!name) return 'Name is required';
  if (name.length > MAX_TOOL_NAME_LENGTH) return `Use ${MAX_TOOL_NAME_LENGTH} characters or fewer`;
  if (!TOOL_NAME_PATTERN.test(name)) {
    return 'Use lowercase snake_case, starting with a letter (e.g. upsert_customer)';
  }
  return null;
}

/**
 * MCP annotations are derived from the HTTP verb so developers don't hand-set side-effect
 * hints: GET is read-only + idempotent, PUT/DELETE are idempotent, DELETE is destructive,
 * and every tool reaches an external system (openWorldHint).
 */
export function deriveAnnotations(method: string): Record<string, boolean> {
  const verb = method.toUpperCase();
  const annotations: Record<string, boolean> = { openWorldHint: true };
  if (verb === 'GET') {
    annotations.readOnlyHint = true;
    annotations.idempotentHint = true;
  }
  if (verb === 'PUT' || verb === 'DELETE') annotations.idempotentHint = true;
  if (verb === 'DELETE') annotations.destructiveHint = true;
  return annotations;
}

/** GET tools carry args in the query string; everything else sends a JSON body. */
export function inputLocationForMethod(method: string): InputLocation {
  return method.toUpperCase() === 'GET' ? 'query' : 'body';
}

/** Where a row actually rides: GET has no body, so everything is forced to the query string. */
function effectiveLocation(row: ToolInputRow, method: string): InputLocation {
  if (inputLocationForMethod(method) === 'query') return 'query';
  return row.location ?? 'body';
}

export function rowsToRequestSchema(rows: ToolInputRow[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  for (const row of rows) {
    const property: Record<string, unknown> = { type: row.type };
    const description = row.description?.trim();
    if (description) property.description = description;
    properties[row.name] = property;
    // Reach-sourced fields are auto-supplied at request time, so they're always required (their
    // presence is guaranteed) — the agent isn't asked for them, but the request can't omit them.
    if (row.required || row.source) required.push(row.name);
  }
  return { type: 'object', properties, required };
}

export function rowsToRequestMapping(rows: ToolInputRow[], method: string): Record<string, unknown> {
  const naturalLocation = inputLocationForMethod(method);
  const blocks: Record<InputLocation, Record<string, string>> = { body: {}, query: {} };
  for (const row of rows) {
    const value = row.source ? contextToken(row.source) : argTemplate(row.name);
    blocks[effectiveLocation(row, method)][row.name] = value;
  }
  // Always emit the natural location (even empty — that's the blank-draft shape); emit the other
  // location only when a field was explicitly placed there.
  const mapping: Record<string, unknown> = { [naturalLocation]: blocks[naturalLocation] };
  const otherLocation: InputLocation = naturalLocation === 'body' ? 'query' : 'body';
  if (Object.keys(blocks[otherLocation]).length > 0) mapping[otherLocation] = blocks[otherLocation];
  return mapping;
}

export function requestSchemaToRows(
  schema: Record<string, unknown> | null | undefined,
  mapping?: Record<string, unknown> | null,
  method?: string,
): ToolInputRow[] {
  if (!schema || typeof schema !== 'object') return [];
  const properties = (schema.properties ?? {}) as Record<string, unknown>;
  const required = new Set(Array.isArray(schema.required) ? (schema.required as string[]) : []);
  const sourceByName = contextSourceByFieldName(mapping);
  const locationByName = locationByFieldName(mapping);
  const naturalLocation = method ? inputLocationForMethod(method) : undefined;
  return Object.entries(properties).map(([name, raw]) => {
    const property = (raw ?? {}) as Record<string, unknown>;
    const type = normalizeType(property.type);
    const row: ToolInputRow = { name, type, required: required.has(name) };
    if (typeof property.description === 'string') row.description = property.description;
    const source = sourceByName.get(name);
    if (source) row.source = source;
    // Record location only as an override of the method's natural placement, so all-body POSTs and
    // all-query GETs round-trip to rows with no location set.
    const location = locationByName.get(name);
    if (location && naturalLocation && location !== naturalLocation) row.location = location;
    return row;
  });
}

function normalizeType(type: unknown): ToolInputType {
  if (type === 'number' || type === 'integer') return 'number';
  if (type === 'boolean') return 'boolean';
  return 'string';
}

function exampleValueForType(type: ToolInputType): unknown {
  if (type === 'number') return 0;
  if (type === 'boolean') return false;
  return '';
}

/**
 * Seeds the test runner's Args editor with the same agent-facing args the MCP server registers
 * (deriveAgentArgsSchema, so the two can't drift): context-sourced fields are omitted — they
 * aren't agent args; the test runner fills them from the simulated context (tenant external ID,
 * sample customer phone).
 */
export function buildArgsExample(
  schema: Record<string, unknown> | null | undefined,
  mapping: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const agentArgsSchema = deriveAgentArgsSchema({ requestSchema: schema, requestMapping: mapping });
  const example: Record<string, unknown> = {};
  for (const row of requestSchemaToRows(agentArgsSchema)) {
    example[row.name] = exampleValueForType(row.type);
  }
  return example;
}

/**
 * True when any of the tool's request templates (mapping, base URL, path) references
 * `{{context.<source>}}` — the run can't succeed without that context value. Mirrors the
 * execution engine's pre-flight check; the test panel uses it to mark the simulated value
 * as required instead of optional.
 */
export function referencesContextSource(templates: unknown, source: ContextSource): boolean {
  return collectTemplateStrings(templates).some(template =>
    extractReferences(template, CONTEXT_REF_RE).has(source),
  );
}

const SCALAR_SCHEMA_TYPES = new Set(['string', 'number', 'integer', 'boolean']);
const ALLOWED_SCHEMA_KEYS = new Set(['type', 'properties', 'required']);
const STRUCTURAL_PROPERTY_KEYS = ['properties', 'items', 'enum', 'oneOf', 'anyOf', 'allOf'];

/**
 * Why the schema + mapping can't be shown in the simple editor, or `null` when they can.
 * The simple editor handles exactly what {@link rowsToRequestMapping} produces: a flat scalar
 * schema where every property maps 1:1 into the body or the query string (non-GET tools may use
 * both; GET has no body) as either an `{{args.<name>}}` value or a Reach-context token
 * ({@link CONTEXT_SOURCES}). The returned reason is shown next to the disabled Simple toggle so
 * partners know what's keeping them in the JSON editor.
 */
export function tableRepresentabilityIssue(
  schema: Record<string, unknown> | null | undefined,
  mapping: Record<string, unknown> | null | undefined,
  method: string,
): string | null {
  if (!schema || typeof schema !== 'object' || schema.type !== 'object') {
    return 'The schema is not a flat object of inputs';
  }

  const unsupportedSchemaKey = Object.keys(schema).find(key => !ALLOWED_SCHEMA_KEYS.has(key));
  if (unsupportedSchemaKey) {
    return `The schema uses "${unsupportedSchemaKey}", which the table can't show`;
  }

  const properties = (schema.properties ?? {}) as Record<string, unknown>;
  const propertyNames = Object.keys(properties);
  for (const [name, raw] of Object.entries(properties)) {
    const property = (raw ?? {}) as Record<string, unknown>;
    const type = property.type;
    const isScalar = typeof type === 'string' && SCALAR_SCHEMA_TYPES.has(type);
    const isStructural = STRUCTURAL_PROPERTY_KEYS.some(key => key in property);
    if (!isScalar || isStructural) {
      return `Input "${name}" has a nested or non-scalar type`;
    }
  }

  const mappingObject = (mapping ?? {}) as Record<string, unknown>;
  const naturalLocation = inputLocationForMethod(method);
  // Non-GET tools may split fields between the body and the query string; GET has no body.
  const allowedLocations: string[] = naturalLocation === 'body' ? ['body', 'query'] : ['query'];
  const unsupportedMappingKey = Object.keys(mappingObject).find(key => !allowedLocations.includes(key));
  if (unsupportedMappingKey === 'headers') {
    return 'Custom headers can only be edited as JSON';
  }
  if (unsupportedMappingKey) {
    return naturalLocation === 'body'
      ? 'Fields are mapped outside the body or query'
      : 'Fields are mapped outside the request query';
  }

  const propertyNameSet = new Set(propertyNames);
  const mappedNames = new Set<string>();
  for (const location of allowedLocations) {
    const fields = (mappingObject[location] ?? {}) as Record<string, unknown>;
    for (const [name, value] of Object.entries(fields)) {
      const mapsToArg = value === argTemplate(name);
      const mapsToContext = contextSourceForValue(value) !== undefined;
      if ((!mapsToArg && !mapsToContext) || !propertyNameSet.has(name)) {
        return `"${name}" does not map 1:1 to an input`;
      }
      if (mappedNames.has(name)) {
        return `"${name}" is mapped more than once`;
      }
      mappedNames.add(name);
    }
  }
  for (const name of propertyNames) {
    if (!mappedNames.has(name)) {
      return `Input "${name}" is not mapped`;
    }
  }
  return null;
}

/** True when {@link tableRepresentabilityIssue} finds nothing blocking the simple editor. */
export function isTableRepresentable(
  schema: Record<string, unknown> | null | undefined,
  mapping: Record<string, unknown> | null | undefined,
  method: string,
): boolean {
  return tableRepresentabilityIssue(schema, mapping, method) === null;
}

export interface InputRowsIssue {
  index: number;
  message: string;
}

/** Inline validation for the inputs table: names must be present, unique, and referenceable. */
export function validateInputRows(rows: ToolInputRow[]): InputRowsIssue[] {
  const issues: InputRowsIssue[] = [];
  const seen = new Map<string, number>();
  rows.forEach((row, index) => {
    // Validate the raw name, not a trimmed copy: it becomes a schema property key and the
    // `{{args.<name>}}` reference verbatim, so surrounding whitespace must be rejected here.
    const name = row.name;
    if (!name.trim()) {
      issues.push({ index, message: 'Name is required' });
      return;
    }
    if (!INPUT_NAME_PATTERN.test(name)) {
      issues.push({ index, message: 'Use letters, numbers, _ or - only' });
      return;
    }
    if (seen.has(name)) {
      issues.push({ index, message: `Duplicate name "${name}"` });
      return;
    }
    seen.set(name, index);
  });
  return issues;
}
